import { AxiosError } from "axios";
import { UseFormSetError } from "react-hook-form";

import { AlertTypes } from "./atoms";

// Error types for login error handling
export type LoginErrorType =
  | "INVALID_CREDENTIALS"
  | "ACCOUNT_LOCKED"
  | "SERVER_ERROR"
  | "UNKNOWN_ERROR";

export interface LoginError {
  status?: number;
  message: string;
  type?: LoginErrorType;
}

// Error message mappings for login
export const LOGIN_ERROR_MESSAGES = {
  INVALID_CREDENTIALS: "newWelcome.logInForm.invalidCredentialsError",
  ACCOUNT_LOCKED: "user-account.personalDetails.loginData.accountLocked",
  SERVER_ERROR: "translation:Common.generalForm.error.genericError",
} as const;

// Error detection patterns for login
export const LOGIN_ERROR_PATTERNS = {
  INVALID_CREDENTIALS: "Bad credentials",
  ACCOUNT_LOCKED: "Maximale Anzahl an Login-Versuchen erreicht",
  INVALID_VERIFICATION_CODE: "invalid code",
  VERIFICATION_LOCKED: "Max amount of validation tries reached.",
  VERIFICATION_CODE_LOCKED: "Max amount of active codes reached",
} as const;

// Form fields type for login form
export type LoginFormFields = {
  emailAddress: string;
  password: string;
};

interface AxiosErrorResponseData {
  error?: string;
  error_description?: string;
  message?: string;
}

// Determine login error type based on error message and status
export const determineLoginErrorType = (rawError: unknown): LoginErrorType => {
  let error: LoginError = {
    message: "Unknown error",
  };

  if (isAxiosError(rawError)) {
    const data = rawError.response?.data as AxiosErrorResponseData | undefined;

    error = {
      status: rawError.response?.status,
      message:
        data?.message ||
        data?.error_description ||
        data?.error ||
        rawError.message,
    };
  } else if (isLoginError(rawError)) {
    error = rawError;
  }

  // Check if error has a specific type already set
  if (error.type) {
    return error.type;
  }

  const normalizedMessage = error.message.toLowerCase();

  if (error.status === 400) {
    if (
      normalizedMessage.includes(
        LOGIN_ERROR_PATTERNS.INVALID_CREDENTIALS.toLowerCase(),
      )
    ) {
      return "INVALID_CREDENTIALS";
    }
    if (
      normalizedMessage.includes(
        LOGIN_ERROR_PATTERNS.ACCOUNT_LOCKED.toLowerCase(),
      )
    ) {
      return "ACCOUNT_LOCKED";
    }
  }

  if (error.status === 500) {
    return "SERVER_ERROR";
  }

  return "UNKNOWN_ERROR";
};

const isAxiosError = (error: any): error is AxiosError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "isAxiosError" in error &&
    (error as AxiosError).isAxiosError === true
  );
};

const isLoginError = (error: any): error is LoginError => {
  return (
    typeof error === "object" &&
    error !== null &&
    typeof error.message === "string" &&
    !("isAxiosError" in error) // Exclude AxiosError objects
  );
};

// Set form errors for both email and password fields
export const setLoginFormErrors = (
  errorType: "INVALID_CREDENTIALS" | "ACCOUNT_LOCKED",
  setError: UseFormSetError<any>,
  t: (key: string) => string,
  emailFieldName: string = "emailAddress",
) => {
  const errorMessage = t(LOGIN_ERROR_MESSAGES[errorType]);
  setError(emailFieldName, { message: errorMessage });
  setError("password", { message: errorMessage });
};

// Main login error handler function
export const createLoginErrorHandler = (
  setError: UseFormSetError<any>,
  t: (key: string) => string,
  showAlert: (alert: { text: string; type: any }) => void,
  emailFieldName: string = "emailAddress",
) => {
  return (error: unknown) => {
    const errorType = determineLoginErrorType(error);

    switch (errorType) {
      case "INVALID_CREDENTIALS":
        setLoginFormErrors(errorType, setError, t, emailFieldName);
        break;
      case "ACCOUNT_LOCKED":
        setLoginFormErrors(errorType, setError, t, emailFieldName);
        showAlert({
          text: t(LOGIN_ERROR_MESSAGES.ACCOUNT_LOCKED),
          type: AlertTypes.error,
        });
        break;

      case "SERVER_ERROR":
        showAlert({
          text: t(LOGIN_ERROR_MESSAGES.SERVER_ERROR),
          type: AlertTypes.error,
        });
        break;

      default:
        showAlert({
          text: t(LOGIN_ERROR_MESSAGES.SERVER_ERROR),
          type: AlertTypes.error,
        });
        break;
    }
  };
};
